import { FC } from "react";
import { styled } from "styled-components";
import { button, buttonReset, colors, font } from "../../lib/sharedStyles";
import { useSearchspringPLPContext } from "../../contexts/SearchspringPLPContext";
import { X } from "@fenderdigital/ui/Icons";

const FixedContainer = styled.div`
  position: fixed;
  z-index: 10;
  bottom: 0;
  width: 100%;
  height: 72px;
  background-color: ${colors.fender.white};
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (min-width: 1024px) {
    height: 104px;
    padding: 24px;
  }
`;

const Prompt = styled.p`
  ${font('Instrument Sans', 600, '14px', '20px', '0.14px')}
  color: ${colors.fender.black};
  margin: 0;
  padding-right: 24px;

  @media (min-width: 1024px) {
    font-size: 16px;
    line-height: 22px;
    letter-spacing: .16px;
  }
`;

const Button = styled.a<{ $disabled: boolean; }>`
  ${buttonReset()};
  ${button('Black Pill', 'fender')};

  @media (max-width: 1023px) {
    min-width: 120px;
    font-weight: 500;
    padding: 10px 15px;
  }

  ${({ $disabled }) => $disabled && `
    background-color: #EFF0F0;
    color: #595959;
    pointer-events: none;
  `}
`;

const ProductsContainer = styled.div`
  width: 100%;
  display: flex;
  gap: 4px;
  margin-right: 24px;
`;

const CompareTile = styled.div<{ $isEmpty?: boolean; }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  width: calc(100% / 2);
  padding: 0 16px;
  background: linear-gradient(0deg, #F7F7F7 0%, #F7F7F7 100%), linear-gradient(182deg, #E5E5E5 -57.65%, #E5E5E5 -36.25%, #EFF0F0 35.48%, #F7F7F7 98.62%);

  @media (min-width: 1024px) {
    width: calc(100% / 4);
  }

  ${({ $isEmpty }) => $isEmpty && `
    justify-content: center;
  `};
`;

const TilePrompt = styled.span`
  display: none;
  ${font('Instrument Sans', 400, '12px', '28px', '0.12px')}
  color: ${colors.fender.primaryDark};

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const TileText = styled.div`
  display: none;
  flex-direction: column;
  margin-left: 8px;

  @media (min-width: 1024px) {
    display: flex;
  }
`;

const ProductName = styled.span`
  ${font('Instrument Sans', 600, '10px', '16px', '0.1px')}
  color: ${colors.fender.primaryDark};
  padding-right: 4px;
`;

const ProductPrice = styled.span`
  ${font('Instrument Sans', 400, '10px', '16px', '0.1px')}
  color: ${colors.fender.primaryDark};
`;

const ProductImage = styled.img`
  width: 100px;
`;

const RemoveButton = styled.button`
  ${buttonReset()};
  width: 16px;
  height: 16px;

  svg {
    width: 100%;
    height: 100%;
  }
`;

interface Props {
  shopifyCollectionHandle: string;
}

const PLPCompareBar: FC<Props> = ({ shopifyCollectionHandle }) => {
  const { compareProducts, compareMax, handleCompareProductClick } = useSearchspringPLPContext();
  const emptyBoxesToRender = compareMax - compareProducts.length;
  let compareUrl = `/pages/compare/${shopifyCollectionHandle}`;
  compareProducts.forEach(product => {
    // @ts-ignore
    compareUrl += `,${product.attributes.handle}`;
  });

  return (
    <FixedContainer>
      {(compareProducts.length > 0) ? (
        <ProductsContainer>
          {compareProducts.map((product: any) => (
            <CompareTile key={product.attributes.ss_id}>
              <ProductImage src={product.attributes.image} />
              <TileText>
                {(product.mappings.core.name.length > 50) ? (
                  <ProductName>{product.mappings.core.name.substring(0, 50)}...</ProductName>
                ) : (
                  <ProductName>{product.mappings.core.name}</ProductName>
                )}
                <ProductPrice>{product.mappings.core.price}</ProductPrice>
              </TileText>
              <RemoveButton onClick={() => handleCompareProductClick(product)}>
                <X />
              </RemoveButton>
            </CompareTile>
          ))}
          {Array.from({length: emptyBoxesToRender}).map((_i, index) => (
            <CompareTile key={`empty-compare-tile-${index}`} $isEmpty>
              {(index == 0 && compareProducts.length == 1) && (
                <TilePrompt>Select one more product to compare</TilePrompt>
              )}
            </CompareTile>
          ))}
        </ProductsContainer>
      ) : (
        <Prompt>Select up to {compareMax} products to compare</Prompt>
      )}
      <Button href={compareUrl} $disabled={(compareProducts.length < 2)}>
        Compare
      </Button>
    </FixedContainer>
  );
};

export default PLPCompareBar;